import { UserContextState } from "@contexts/UserContext";
import { useIntercom } from "react-use-intercom";
import { useEffect, useLayoutEffect } from "react";
import { generateHMAC } from "@utils/hmac";
import { useRouter } from "next/router";
import { useFeatureFlag } from "@src/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";

type UseUpdateIntercomReturn = {
  show: () => void;
};

const useUpdateIntercom = (
  userContext?: UserContextState,
): UseUpdateIntercomReturn => {
  const {
    update: updateIntercom,
    hide: hideIntercom,
    show,
    trackEvent,
  } = useIntercom();
  const router = useRouter();
  const chatKnowledgeBaseEnabled = useFeatureFlag(Ff.ChatKnowledgeBase);

  useEffect(() => {
    if (
      userContext?.user &&
      process.env.NEXT_PUBLIC_VERCEL_ENV === "production" &&
      !userContext.isStaff
    ) {
      const payload = {
        userId: userContext.user.id.toString(),
        userHash: generateHMAC(
          process.env.NEXT_PUBLIC_INTERCOM_SECRET_KEY!,
          userContext.user.id.toString(),
        ),
        email: userContext.user.email || undefined,
        name: userContext.user.name!,
        company: {
          companyId: userContext.user.org!.id.toString(),
          name: userContext.user.org?.name,
        },
      };
      updateIntercom(payload);
      trackEvent("dashboard-loaded");
      hideIntercom();
    }
  }, [userContext]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    // Only hide the default launcher if the ChatKnowledgeBase feature flag is enabled
    // When the flag is enabled, users can access Intercom through the support dropdown
    // When disabled, keep the default launcher visible
    updateIntercom({ hideDefaultLauncher: chatKnowledgeBaseEnabled });
  }, [updateIntercom, router.pathname, chatKnowledgeBaseEnabled]);

  return {
    show,
  };
};

export default useUpdateIntercom;
